import useParseURL from './useParseURL';
import {useFilterContext} from 'context/FilterContext';
import {useGetPlan} from './swr';
import {ActionSpeciesEnum, ForecastStateOpenSpeciesEnum} from 'client';
import {DEFAULT_WEIGHT_UNIT} from 'appConstants';
import {WeightUnits} from 'interfaces';
import {useCallback} from 'react';

export type LweHogFormatter = (unit: number, species?: ActionSpeciesEnum) => number;

export const useGetHogLweFormatter = () => {
  const {planId} = useParseURL();
  const filterContext = useFilterContext();
  const {data: plan} = useGetPlan({resourceId: planId});

  const getHogLweRatio = useCallback(
    (species: ActionSpeciesEnum = ActionSpeciesEnum.Salmon) => {
      const harvestLoss = (species === 'trout' ? plan?.harvestLossTrout : plan?.harvestLossSalmon) ?? 0;
      const lweHogRatio = 1 - harvestLoss;

      return lweHogRatio;
    },
    [plan?.harvestLossSalmon, plan?.harvestLossTrout],
  );

  /**
   * Formats LWE unit to HOG if HOG format is selected in FilterContext. Leaves unit in LWE otherwise
   */
  const formatLweHog: LweHogFormatter = useCallback(
    (unit: number, species: ActionSpeciesEnum = ActionSpeciesEnum.Salmon) => {
      const lweHogRatio = getHogLweRatio(species);
      const weightUnit = filterContext.weightUnit ?? DEFAULT_WEIGHT_UNIT;
      return weightUnit === WeightUnits.hog ? lweHogRatio * unit : unit;
    },
    [filterContext.weightUnit, getHogLweRatio],
  );

  /**
   * Formats HOG units to LWE. Leaves LWE units as is
   */
  const formatToLwe = useCallback(
    (unit: number, species: ForecastStateOpenSpeciesEnum = ForecastStateOpenSpeciesEnum.Salmon) => {
      const lweHogRatio = getHogLweRatio(species);
      const weightUnit = filterContext.weightUnit ?? DEFAULT_WEIGHT_UNIT;
      return weightUnit === WeightUnits.hog ? unit / lweHogRatio : unit;
    },
    [filterContext.weightUnit, getHogLweRatio],
  );

  return {formatLweHog, formatToLwe};
};
